// This helper file implements a staging-only override mechanism for API configuration.
// The app reads developer-provided values from localStorage only when
// REACT_APP_ENVIRONMENT === 'staging'. This prevents accidental production overrides.

export interface OverrideParam {
  partna_v4_baseUrl?: string;
  fusion_baseUrl?: string;
  fusion_tenantId?: string;
  fusion_apiKey?: string;
  [key: string]: string | undefined;
}

export const OVERRIDE_LOCALSTORAGE_KEY = 'partna_v4_params';

export const isStagingEnvironment = (): boolean =>
  process.env.REACT_APP_ENVIRONMENT === 'staging';

export const getOverrideParams = (): OverrideParam | null => {
  if (!isStagingEnvironment()) return null;

  const raw = localStorage.getItem(OVERRIDE_LOCALSTORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const params = parsed as OverrideParam;
    if (params.partna_v4_baseUrl?.trim() === 'https://staging-api.example.com') {
      localStorage.removeItem(OVERRIDE_LOCALSTORAGE_KEY);
      return null;
    }

    return params;
  } catch (error) {
    console.warn('Failed to parse override params from localStorage', error);
    return null;
  }
};

export const getOverrideBaseUrl = (defaultBaseUrl: string): string => {
  const override = getOverrideParams();
  return override?.partna_v4_baseUrl?.trim() || defaultBaseUrl;
};

// Example override object in browser console:
// localStorage.setItem('partna_v4_params', JSON.stringify({
//   partna_v4_baseUrl: 'https://staging-api.example.com',
//   fusion_apiKey: 'your-fusion-api-key',
//   fusion_tenantId: 'your-tenant-id',
//   fusion_baseUrl: 'https://staging-auth.example.com',
// }));

export const getOverrideFusionConfig = () => {
  const override = getOverrideParams();

  return {
    baseUrl:
      override?.fusion_baseUrl ||
      process.env.REACT_APP_FUSION_AUTH_BASE_URL ||
      'https://auth.coinprofile.co',
    apiKey: override?.fusion_apiKey || process.env.REACT_APP_FUSION_API_KEY_V4 || '',
    tenantId: override?.fusion_tenantId || process.env.REACT_APP_FUSION_TENANT_ID_V4 || '',
  };
};
